import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { Play, Pause, Volume2, VolumeX, Maximize, AlertCircle, RefreshCw } from 'lucide-react';

const PromoVideoPlayer = ({ src, poster }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const hlsRef = useRef(null);

  // Customer code for Cloudflare Stream URLs
  const CUSTOMER_CODE = 'xg4xjbetrvqn62vp';

  // Extract video ID from URL
  const getVideoId = (url) => {
    if (!url) return null;
    
    // Match 32-character hex video ID from any URL format
    const match = url.match(/([a-f0-9]{32})/i);
    return match ? match[1] : null;
  };

  // Get HLS URL from video ID
  const getHlsUrl = (url) => {
    const videoId = getVideoId(url);
    if (videoId) {
      return `https://customer-${CUSTOMER_CODE}.cloudflarestream.com/${videoId}/manifest/video.m3u8`;
    }
    // If already an HLS URL
    if (url && url.includes('.m3u8')) {
      return url;
    }
    return null;
  };

  // Get thumbnail URL
  const getThumbnailUrl = (url) => {
    const videoId = getVideoId(url);
    if (videoId) {
      return `https://customer-${CUSTOMER_CODE}.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
    }
    return poster;
  };

  // Retry loading the video
  const retryLoad = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    setIsLoading(true);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    const hlsUrl = getHlsUrl(src);
    console.log('PromoVideoPlayer: Loading video', { src, hlsUrl, videoId: getVideoId(src) });
    
    if (!hlsUrl) {
      setError('Invalid video URL');
      setIsLoading(false);
      return;
    }

    // Clean up any existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setIsLoading(true);
    setError(null);

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        startLevel: -1, // Auto quality
        debug: false,
        xhrSetup: (xhr) => {
          xhr.withCredentials = false;
        }
      });
      
      hlsRef.current = hls;
      
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('PromoVideoPlayer: Video loaded successfully');
        setError(null);
        setIsLoading(false);
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('PromoVideoPlayer HLS error:', data);
        if (data.fatal) {
          setIsLoading(false);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (retryCount < 2) {
              // Auto retry once
              setTimeout(() => {
                hls.startLoad();
                setRetryCount(prev => prev + 1);
              }, 1000);
            } else {
              setError('Unable to load video. Please try again.');
            }
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            setError('Video playback error');
          }
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = hlsUrl;
      video.addEventListener('loadeddata', () => {
        setIsLoading(false);
        setError(null);
      });
      video.addEventListener('error', () => {
        setError('Failed to load video');
        setIsLoading(false);
      });
    } else {
      setError('Your browser does not support video playback');
      setIsLoading(false);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, retryCount]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      video.play().catch(err => console.error('Play error:', err));
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = () => {
    const container = videoRef.current?.parentElement?.parentElement;
    if (!container) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen?.() || container.webkitRequestFullscreen?.();
    }
  };

  const thumbnailUrl = getThumbnailUrl(src);

  return (
    <div 
      className="relative w-full h-full bg-black group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        playsInline
        poster={thumbnailUrl}
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        crossOrigin="anonymous"
      />
      
      {/* Loading indicator */}
      {isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="w-10 h-10 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Error state with retry button */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center p-4">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
            <p className="text-white text-sm mb-3">{error}</p>
            <button 
              onClick={retryLoad}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      )}
      
      {/* Play button overlay */}
      {!isPlaying && !error && !isLoading && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
          onClick={togglePlay}
        >
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
            <Play className="w-8 h-8 text-white ml-1" fill="white" />
          </div>
        </div>
      )}
      
      {/* Controls */}
      {(showControls || !isPlaying) && !error && !isLoading && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-4">
            <button onClick={togglePlay} className="text-white hover:text-gray-300 transition-colors">
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={toggleMute} className="text-white hover:text-gray-300 transition-colors">
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <div className="flex-1" />
            <button onClick={toggleFullscreen} className="text-white hover:text-gray-300 transition-colors">
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoVideoPlayer;
